<!-- Copyright (c) 2023, Alix Mougenot (Au pied des Arbres), alix.mougenot@gmail.com -->
<!-- See license file. -->
<script setup lang="ts">
import { provide, computed, onMounted, watch, ref } from "vue";
import { RouterView, useRouter } from "vue-router";
import {
  DefaultApolloClient,
  provideApolloClient,
} from "@vue/apollo-composable";
import { useToast } from "vue-toast-notification";
import "vue-toast-notification/dist/theme-bootstrap.css";
import * as Sentry from "@sentry/vue";

import LoginHeader from "./components/LoginHeader.vue";
import NavigationBar from "./components/NavigationBar.vue";

import { setupQueries } from "./logic/queries";

import {
  actorsKey,
  dentistKey,
  filesKey,
  templatesKey,
  googleClientIDKey,
  mutateDentistKey,
  mutateCaseKey,
  mutateActorKey,
  mutateEventKey,
  mutateTemplateKey,
  createEventKey,
  createActorKey,
  createCaseKey,
  createTemplateKey,
  membersKey,
  errorHandlerKey,
  healthCentersKey,
  mutateHealthCenterKey,
  modalActiveKey,
  importsKey,
} from "./logic/provide";
import type {
  Actor,
  ActorEdit,
  CDOMember,
  Case,
  CaseEdit,
  Dentist,
  DentistEdit,
  EventCreation,
  EventEdit,
  HealthCenter,
  HealthCenterEdit,
  Template,
  TemplateCategory,
  TemplateSubCategory,
  TemplateEdit,
  TemplateCreation,
  Import,
} from "./types";
import { useLoginStore } from "./stores/login";
import {
  ApolloClient,
  ApolloLink,
  InMemoryCache,
  concat,
  createHttpLink,
} from "@apollo/client/core";

// Modal
const modalActive = ref(false);
const modal = ref(null as HTMLDivElement | null);
provide(modalActiveKey, modalActive);
watch(modalActive, (value) => {
  if (value && modal.value) {
    modal.value.scrollTo(0, 0);
  }
});

// Error handling
const toast = useToast();
const errorHandler = (error: string, details?: Error) => {
  if (details instanceof Error) {
    Sentry.captureException(details);
    console.error(error, details);
  } else {
    Sentry.captureMessage(error, "error");
  }
  toast.error(error, {
    duration: 30000,
  });
};
provide(errorHandlerKey, errorHandler);

// Apollo Setup
const gql_url = import.meta.env.VITE_BACKEND_URL + "/query";
const httpLink = createHttpLink({
  uri: gql_url,
});

const userStore = useLoginStore();
const authMiddleware = new ApolloLink((operation, forward) => {
  const token = userStore.currentUser?.token;
  if (token) {
    operation.setContext({
      headers: {
        authorization: `Bearer ${token}`, // TODO: Use secure cookies instead of tokens
      },
    });
  }
  return forward(operation);
});
const cache = new InMemoryCache();
const apolloClient = new ApolloClient({
  link: concat(authMiddleware, httpLink),
  cache,
});
provide(DefaultApolloClient, apolloClient);
provideApolloClient(apolloClient);

// Loading all major datasets at boot via provide/inject.
// Providing all mutations at boot via provide/inject.
// All data is local -- except for Events that only exist inside one particular view --
// this design is for the search and deeplinks to work properly without constant calls to the backend.
// This stategy will not work for ever, when loading performances become a problem,
// refactor search and deeplinks to become backend dependent.

const {
  actorMutation,
  actorsQuery,
  caseMutation,
  createActorMutation,
  createCaseMutation,
  createEventMutation,
  dentistMutation,
  dentistQuery,
  eventMutation,
  filesQuery,
  healthCenterMutation,
  healthCentersQuery,
  importsQuery,
  membersQuery,
  templateCreationMutation,
  templateMutation,
  templatesQuery,
} = setupQueries(apolloClient);

// Read only data

const dentists = computed(() => {
  if (dentistQuery.error.value) {
    errorHandler(
      "Erreur lors du chargement des praticiens",
      dentistQuery.error.value
    );
    return { list: [], index: new Map<string, number>() };
  }
  const list = (dentistQuery.result.value?.dentists as Array<Dentist>) ?? [];
  const map = new Map<string, number>();
  list.forEach((val, index) => {
    map.set(val.id, index);
  });
  return { list, index: map };
});

const actors = computed(() => {
  if (actorsQuery.error.value) {
    errorHandler(
      "Erreur lors du chargement des requérants",
      actorsQuery.error.value
    );
    return { list: [], index: new Map<string, number>() };
  }
  const list = (actorsQuery.result.value?.actors as Array<Actor>) ?? [];
  const map = new Map<string, number>();
  list.forEach((val, index) => {
    map.set(val.id, index);
  });
  return { list, index: map };
});

const healthCenters = computed(() => {
  if (healthCentersQuery.error.value) {
    errorHandler(
      "Erreur lors du chargement des centres de santé",
      healthCentersQuery.error.value
    );
    return { list: [], index: new Map<string, number>() };
  }
  const list =
    (healthCentersQuery.result.value?.centers as Array<HealthCenter>) ?? [];
  const map = new Map<string, number>();
  list.forEach((val, index) => {
    map.set(val.id, index);
  });
  return { list, index: map };
});

const cases = computed(() => {
  if (filesQuery.error.value) {
    errorHandler(
      "Erreur lors du chargement des dossiers",
      filesQuery.error.value
    );
    return { list: [], index: new Map<string, number>() };
  }
  const list = (filesQuery.result.value?.cases as Array<Case>) ?? [];
  const map = new Map<string, number>();
  list.forEach((val, index) => {
    map.set(val.id, index);
  });
  return { list, index: map };
});

const members = computed(() => {
  if (membersQuery.error.value) {
    errorHandler(
      "Erreur lors du chargement des conseillers",
      membersQuery.error.value
    );
    return { index: new Map<string, CDOMember>(), options: [] };
  }
  const list = (membersQuery.result.value?.members as Array<CDOMember>) ?? [];
  const map = new Map<string, CDOMember>();
  const options = new Array<{ value: string; text: string }>();
  options.push({ value: "", text: "Non défini" });
  list.forEach((val) => {
    map.set(val.id, val);
    options.push({ value: val.id, text: val.display_name });
    if (val.legacy_id) {
      map.set(val.legacy_id, val);
    }
  });
  return { index: map, options };
});

const imports = computed(() => {
  if (importsQuery.error.value) {
    errorHandler(
      "Erreur lors du chargement des dates des imports",
      importsQuery.error.value
    );
    return [];
  }
  return (importsQuery.result.value?.imports as Array<Import>) ?? [];
});

const templates = computed(() => {
  if (templatesQuery.error.value) {
    errorHandler(
      "Erreur lors du chargement des templates",
      templatesQuery.error.value
    );
    return [];
  }
  return (templatesQuery.result.value?.templates as Array<Template>) ?? [];
});

const templates_sorted = computed(() => {
  const hierarchy = {} as Record<
    TemplateCategory,
    { [_ in TemplateSubCategory]: Array<string> }
  >;
  const index = new Map<string, number>();
  templates.value.forEach((template, i) => {
    index.set(template.id, i);
    const { category, sub_category } = template;
    if (!hierarchy[category]) {
      var obj = {} as Record<TemplateSubCategory, Array<string>>;
      obj[sub_category] = [template.id];
      hierarchy[category] = obj;
    } else if (hierarchy[category][sub_category]) {
      hierarchy[category][sub_category].push(template.id);
    } else {
      hierarchy[category][sub_category] = [template.id];
    }
  });
  return {
    categories: hierarchy,
    index,
    list: templates.value,
  };
});

provide(actorsKey, actors);
provide(dentistKey, dentists);
provide(healthCentersKey, healthCenters);
provide(filesKey, cases);
provide(membersKey, members);
provide(importsKey, imports);
provide(templatesKey, templates_sorted);

// Mutations
provide(mutateDentistKey, (id: String, edit: DentistEdit) => {
  const mutationResult = dentistMutation.mutate({
    id,
    dentist: edit,
  });
  mutationResult.catch((e) => {
    errorHandler("Erreur lors de la modification du praticien", e);
  });
  return mutationResult;
});
provide(mutateHealthCenterKey, (id: String, edit: HealthCenterEdit) => {
  const mutationResult = healthCenterMutation.mutate({
    id,
    center: edit,
  });
  mutationResult.catch((e) => {
    errorHandler("Erreur lors de la modification du centre de santé", e);
  });
  return mutationResult;
});
provide(mutateActorKey, (id: String, edit: ActorEdit) => {
  const mutationResult = actorMutation.mutate({
    id,
    actor: edit,
  });
  mutationResult.catch((e) => {
    errorHandler("Erreur lors de la modification du requérant", e);
  });
  return mutationResult;
});
provide(createActorKey, (edit: ActorEdit) => {
  const mutationResult = createActorMutation.mutate({
    actor: edit,
  });
  mutationResult.catch((e) => {
    errorHandler("Erreur lors de la création du requérant", e);
  });
  return mutationResult;
});
provide(mutateCaseKey, (id: String, edit: CaseEdit) => {
  const mutationResult = caseMutation.mutate({
    id,
    edit,
  });
  mutationResult.catch((e) => {
    errorHandler("Erreur lors de la modification du dossier", e);
  });
  return mutationResult;
});
provide(createCaseKey, (edit: CaseEdit) => {
  const mutationResult = createCaseMutation.mutate({
    edit,
  });
  mutationResult.catch((e) => {
    errorHandler("Erreur lors de la création du dossier", e);
  });
  return mutationResult;
});

provide(mutateEventKey, (id: String, edit: EventEdit) => {
  const mutationResult = eventMutation.mutate({
    id,
    event: edit,
  });
  mutationResult.catch((e) => {
    errorHandler("Erreur lors de la modification de l'événement", e);
  });
  return mutationResult;
});

provide(createEventKey, (edit: EventCreation) => {
  const mutationResult = createEventMutation.mutate({
    event: edit,
  });
  mutationResult.catch((e) => {
    errorHandler("Erreur lors de la création de l'événement", e);
  });
  return mutationResult;
});

provide(mutateTemplateKey, (id: string, edit: TemplateEdit) =>
  templateMutation.mutate({
    id,
    template: edit,
  })
);

provide(createTemplateKey, (edit: TemplateCreation) =>
  templateCreationMutation.mutate({
    template: edit,
  })
);

const isLoading = computed(() => isLogged.value && filesQuery.loading.value);

// Google Auth (see LoginHeader.vue for more details)
const google_client_id = import.meta.env.VITE_GOOGLE_CLIENT_ID as string;
provide(googleClientIDKey, google_client_id);
const isLogged = computed(() => userStore.currentUser?.token !== undefined);

watch(isLogged, (value) => {
  if (value) {
    console.log("Loading data");
    actorsQuery.load();
    dentistQuery.load();
    healthCentersQuery.load();
    filesQuery.load();
    membersQuery.load();
    templatesQuery.load();
    importsQuery.load();
  }
});

onMounted(() => {
  const meta = document.createElement("meta");
  meta.name = "google-signin-client_id";
  meta.content = google_client_id;
  document.head.appendChild(meta);

  const metaCSP = document.createElement("meta");
  const scriptHosts = import.meta.env.PROD
    ? "https://accounts.google.com https://cahier-jaune.cdocdparis.org"
    : "https://accounts.google.com http://localhost:5173";

  const defaultHosts = import.meta.env.PROD
    ? "https://accounts.google.com https://o4506706338316288.ingest.sentry.io https://cahier-jaune.cdocdparis.org"
    : "https://accounts.google.com https://o4506706338316288.ingest.sentry.io http://localhost:8080 http://localhost:5173";
  metaCSP.httpEquiv = "Content-Security-Policy";
  metaCSP.content = `default-src 'self' ${defaultHosts}; script-src 'self' ${scriptHosts}; worker-src 'self' blob: ; style-src 'self' 'unsafe-inline' https://accounts.google.com; font-src 'self'; img-src 'self' data:;`;
  document.head.appendChild(metaCSP);

  const script = document.createElement("script");
  script.src = "https://accounts.google.com/gsi/client";
  script.async = true;
  script.defer = true;
  document.head.appendChild(script);
});

// Auto removal of modal when navigating
const router = useRouter();
router.beforeEach(() => {
  modalActive.value = false;
  return true;
});
</script>

<template>
  <header class="container-header">
    <LoginHeader />
  </header>
  <div class="container-nav">
    <NavigationBar />
  </div>
  <div class="container-content">
    <div
      class="modal"
      id="modal"
      ref="modal"
      :class="{ hide: !modalActive }"
    ></div>
    <div class="columns" :class="{ hide: modalActive }">
      <div id="nav-col0"></div>
      <div id="nav-col1"></div>
      <div id="nav-col2" class="notLoaded"></div>
      <div id="nav-col3" class="notLoaded"></div>
      <div id="nav-col4" class="notLoaded"></div>
      <div id="nav-col5" class="notLoaded"></div>
      <div id="nav-col6" class="notLoaded"></div>
    </div>
    <div v-if="userStore.currentUser?.name" :class="{ hide: modalActive }">
      <h2 v-if="isLoading">Chargement en cours</h2>
      <RouterView v-else-if="isLogged" />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.columns {
  display: flex;
  flex-direction: row;
}

header {
  position: fixed;
  z-index: 10;
  top: 0;
  left: 0;
  width: 100%;
  height: var(--vp-header-height);
  padding: 1em 0;
  border-bottom: 1px solid var(--color-border);
  background-color: var(--color-background);
}

.container-nav {
  position: fixed;
  flex-direction: row;
  top: var(--vp-header-height);
  left: 0;
  width: var(--vp-menu-width);
  min-width: var(--vp-menu-width);
  height: 100%;
  border-right: 1px solid var(--color-border);
  padding-left: var(--vp-margin-right);
}

.hide {
  display: none;
}

.container-content {
  position: fixed;
  top: var(--vp-header-height);
  left: var(--vp-menu-width);
  overflow-y: scroll;
  height: calc(100% - var(--vp-header-height));
  width: calc(100% - var(--vp-menu-width));

  &.hide {
    display: none;
  }

  .modal {
    position: relative;
    display: flex;
    flex-direction: column;
    z-index: 1;

    &.hide {
      display: none;
    }
  }
}

.notLoaded {
  display: none;
  min-width: 680px; // avoids a too vilolent reflow when the column is loaded
}

@media print {
  .container-nav {
    display: none;
  }
  .container-header {
    display: none;
  }

  .container-content {
    height: calc(29.7cm - 1.1cm);
    width: calc(21cm - 2cm);

    margin-top: 0;
    margin-left: 0;

    top: 0;
    left: 0;
  }
}
</style>
